import { RegisterOptions } from 'react-hook-form'
import { DateFieldSchema, DateOfBirthFieldSchema, DropdownFieldSchema, DropdownItems, FieldType, InputFieldSchema } from '@afterpaytouch/form'
import { IdentityDocument, SupportedLocale } from '../../model'
import { useIsPartialVerified } from '../../state'
import { TFunction } from 'next-i18next'
import { useContext } from 'react'
import { IsRetryPageContext } from './helper'

interface InputFieldSchemaOpts {
  label: string
  value?: string
  options?: RegisterOptions
  inline?: boolean | number
  otherProps?: Record<string, unknown>
  error?: boolean
  show?: boolean
}

export const getInputFieldSchema = ({
  value = '',
  label,
  inline = false,
  options = {},
  otherProps = {},
  error = false,
  show = true,
}: InputFieldSchemaOpts): InputFieldSchema => ({
  type: FieldType.Input,
  show,
  props: {
    label,
    value,
    error,
    ...otherProps,
  },
  inline,
  options,
})

interface InputBirthdaySchemaOpts {
  label: string
  value?: string
  options?: RegisterOptions
  inline?: boolean
  locale?: string
  otherValidations: Omit<DateOfBirthFieldSchema, 'type'>
  otherProps?: Record<string, unknown>
  error?: boolean
  show?: boolean
}

export const getInputBirthdaySchema = ({
  locale,
  label,
  value,
  options = {},
  otherValidations = {},
  otherProps = {},
  error = false,
  show = true,
}: InputBirthdaySchemaOpts): DateOfBirthFieldSchema => ({
  type: FieldType.DateOfBirth,
  show,
  props: {
    label,
    defaultValue: value,
    locale: locale as SupportedLocale,
    error,
    ...otherProps,
  },
  options,
  ...otherValidations,
})

interface DropdownSchemaOpts {
  label: string
  value?: string
  items: DropdownItems[] | null
  options: RegisterOptions
  otherProps?: Record<string, unknown>
  id?: string
  error?: boolean
  show?: boolean
}

export const getDropdownFieldSchema = ({
  label,
  value = '',
  items,
  options,
  id,
  error = false,
  show = true,
  otherProps,
}: DropdownSchemaOpts): DropdownFieldSchema => ({
  type: FieldType.Dropdown,
  props: {
    label,
    defaultValue: value,
    id,
    error,
    ...otherProps,
  },
  items: items ?? [],
  options,
  show,
})

interface DateSchemaOpts {
  label: string
  options: RegisterOptions
  format?: string
  otherProps?: Record<string, unknown>
  error?: boolean
  show?: boolean
}

export const getDateSchema = ({ format, label, options, otherProps = {}, error = false, show = true }: DateSchemaOpts): DateFieldSchema => ({
  type: FieldType.Date,
  format,
  props: {
    label,
    error,
    ...otherProps,
  },
  options,
  show,
})

export const useHeadingAndDescription = (t: TFunction, documentType: IdentityDocument): { heading: string; description: string } => {
  const isRetryPage = useContext(IsRetryPageContext)
  const isRetryMode = useIsPartialVerified() && isRetryPage

  const heading = isRetryMode
    ? t('verifyIdentity:partialVerified:heading')
    : documentType === 'passport'
    ? t('verifyIdentity:document:passport')
    : t('verifyIdentity:document:licence')

  const description =
    documentType === 'passport' ? t('verifyIdentity:partialVerified:description:passport') : t('verifyIdentity:partialVerified:description:licence')

  return {
    heading,
    description,
  }
}
